import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { Config } from '@environments/config';
import { Commands } from '@local/client-contracts';
import { PopupRef } from '@local/ui-infra';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ContextMenuComponent, ContextMenuData, ContextMenuItem, ContextMenuService } from '@shared/components';
import { getCodeLanguage } from '@shared/consts/file-types';
import { EventsService } from '@shared/services';
import { CustomKeyboardEvent, KeyboardService } from '@shared/services/keyboard.service';
import { SessionService } from '@shared/services/session.service';
import { StreamContentService } from '@shared/services/stream-content.service';
import { isRightClick } from '@shared/utils';
import { KeyName, keyCodes } from '@local/ts-infra';
import { stopEvent } from '@shared/utils/elements-util';
import hljs from 'highlight.js';
import { decode } from 'html-entities';
import { CodeJarContainer } from 'ngx-codejar';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { PreviewItemModel } from '../../file-preview.component';
import { BasePreviewComponent } from '../base-preview/base-preview.component';

@UntilDestroy()
@Component({
  selector: 'code-preview',
  templateUrl: './code-preview.component.html',
  styleUrls: ['./code-preview.component.scss', '../../../file-preview-shared.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodePreviewComponent extends BasePreviewComponent {
  private readonly TEXT_BETWEEN_TAGS_RGX = new RegExp(/>([^<]*)</, 'gi');
  private keyHandlerId: string;
  private selectionSnippet = '';
  private isSameSelection: boolean;
  readonly FOOTER_PADDING = 19;
  activeContextMenu: PopupRef<ContextMenuComponent, ContextMenuData>;
  code: string;

  @Input() footerHeight: number;
  @Input() set model(model: PreviewItemModel) {
    this._model = model;
    if (!this.modelValidated()) {
      return;
    }
    this.loadFile();
    this.cdr.markForCheck();
  }

  private get eventResources() {
    return [
      {
        id: this.model?.item.model.id,
        appId: this.model?.item.model.link?.appId,
        type: this.model?.item.model.resource?.type,
        linkId: this.model?.item.model.link?.id,
      },
    ];
  }

  private get location() {
    return { title: window.location.pathname.replace(/^[\/\s]/gm, '') };
  }

  get model(): PreviewItemModel {
    return this._model;
  }

  get paddingBottom(): string {
    return `${this.footerHeight + this.FOOTER_PADDING}px`;
  }

  constructor(
    protected cdr: ChangeDetectorRef,
    protected session: SessionService,
    private keyboardService: KeyboardService,
    private contextMenuService: ContextMenuService,
    private hubService: HubService,
    private commandService: CommandsService,
    private eventsService: EventsService,
    protected streamContentService: StreamContentService
  ) {
    super(cdr, session, streamContentService);
    this.registerKeyboardHandler();
  }

  @ViewChild('codejar') codejarRef: CodeJarContainer;

  ngOnDestroy(): void {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
      this.keyHandlerId = null;
    }
  }

  private loadFile() {
    if (!this.model.item.srcUrl) return;
    this.streamContentService
      .streamTextSrc(this.model.item.srcUrl, Config.preview.files.maxSizeInMB)
      .then((text: string) => {
        let newCode = hljs.highlight(text, {
          language: getCodeLanguage(this.model.item.model.view.title.text),
        }).value;

        const itemHighlights = this.model?.item?.model?.highlights;
        if (itemHighlights?.length) {
          itemHighlights.forEach((word) => {
            newCode = this.replace(newCode, word);
          });
        }
        this.code = newCode;
        this.finishLoad();
      })
      .catch(() => {
        this.errorType = 'loadingError';
        this.finishLoad();
      });
    this.cdr.markForCheck();
  }

  highlightMethod(editor: CodeJarContainer) {
    if (editor.textContent !== null && editor.textContent !== undefined) {
      editor.innerHTML = editor.textContent;
    }
  }

  private registerKeyboardHandler() {
    if (this.keyHandlerId) {
      this.keyboardService.unregisterKeyHandler(this.keyHandlerId);
    }
    this.keyHandlerId = this.keyboardService.registerKeyHandler((keys, event) => this.handleKeys(keys, event), 10);
  }

  private handleKeys(keys: Array<KeyName>, event: CustomKeyboardEvent): void {
    if (keys[0] === 'c' && keys[1] === keyCodes.commandOrControl.toLowerCase() && this.selectionSnippet) {
      event.stopPropagation();
      return;
    } else if (this.selectionSnippet) {
      stopEvent(event);
    }
  }

  mouseup(event: MouseEvent) {
    const prevSelectionSnippet = this.selectionSnippet;
    this.selectionSnippet = window.getSelection().toString();
    if (!this.selectionSnippet) {
      this.codejarRef['editor']['nativeElement']?.blur();
      return;
    }
    stopEvent(event);
    if (prevSelectionSnippet !== this.selectionSnippet) {
      this.isSameSelection = true;
    }
  }

  mousedown(event: MouseEvent) {
    if (!this.selectionSnippet) return;
    stopEvent(event);
  }

  auxclick(event: PointerEvent) {
    if (!isRightClick(event) || !this.selectionSnippet) return;
    stopEvent(event);
    this.openContextMenu(event);
  }

  onClick(event: PointerEvent) {
    event.stopPropagation();
    if (this.isSameSelection) {
      this.isSameSelection = false;
      return;
    }
    if (!this.selectionSnippet) return;
    stopEvent(event);
    this.selectionSnippet = '';
    window.getSelection().removeAllRanges();
    this.hubService.changeFocusState(true, false);
  }

  private async openContextMenu(event: { x: number; y: number }) {
    const openAt: { x: number; y: number } = {
      x: event.x,
      y: event?.y,
    };
    const items = await this.buildContextMenuItems();
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ContextMenuItem) => {
          this.commandService.executeCommand(item.command);
          this.selectionSnippet = '';
        },
      },
      { position: 'below' }
    );
    this.eventsService.event('results.action', { label: 'copy', location: this.location, resources: this.eventResources });
    this.cdr.markForCheck();
  }

  private buildContextMenuItems(): ContextMenuItem[] {
    const items: ContextMenuItem[] = [];
    items.push({
      id: 'copy',
      text: 'Copy',
      command: { type: 'copy-clipboard', value: this.selectionSnippet } as Commands.CopyClipboard,
      shortcut: ['CommandOrControl', 'c'] as KeyName[],
      icon: { type: 'font-icon', value: 'icon-duplicate' },
    });
    return items;
  }

  private replace(text: string, word: string): string {
    return text.replace(this.TEXT_BETWEEN_TAGS_RGX, (match) => {
      const regex = new RegExp(word, 'gi');
      const newText = decode(match, { level: 'all' });
      return newText.replace(regex, (match: string) => {
        return `<mark class="text-highlight">${match}</mark>`;
      });
    });
  }
}
